import a from '@/utils/axios/axios'

const MARK_TYPE_FILTER_CUTOFF = new Date(2026, 0, 1).getTime()

function rangeSegments(range) {
  const start = Number(range?.start)
  const end = Number(range?.end)

  if(!start || !end) return [{ start: range?.start, end: range?.end, useMarkType: true }]
  if(end < MARK_TYPE_FILTER_CUTOFF) return [{ start, end, useMarkType: true }]
  if(start >= MARK_TYPE_FILTER_CUTOFF) return [{ start, end, useMarkType: false }]

  return [
    { start, end: MARK_TYPE_FILTER_CUTOFF - 1, useMarkType: true },
    { start: MARK_TYPE_FILTER_CUTOFF, end, useMarkType: false }
  ]
}

function runValue(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function mergeAverage(existingValue, existingCount, nextValue, nextCount) {
  const existing = runValue(existingValue)
  const next = runValue(nextValue)

  if(existing === null) return next
  if(next === null) return existing

  return ((existing * existingCount) + (next * nextCount)) / (existingCount + nextCount)
}

function mergeRunRows(existing, next) {
  const existingCount = runValue(existing.num_of_runs) || 0
  const nextCount = runValue(next.num_of_runs) || 0
  const merged = { ...existing, ...next }

  Object.keys({ ...existing, ...next }).forEach(key => {
    if(key === 'num_of_runs') {
      merged[key] = existingCount + nextCount
      return
    }

    if(key.startsWith('avg_') && existingCount + nextCount > 0) {
      merged[key] = mergeAverage(existing[key], existingCount, next[key], nextCount)
      return
    }

    if(key.startsWith('min_')) {
      const existingMin = runValue(existing[key])
      const nextMin = runValue(next[key])
      if(existingMin === null) merged[key] = nextMin
      else if(nextMin === null) merged[key] = existingMin
      else merged[key] = Math.min(existingMin, nextMin)
    }
  })

  return merged
}

function mergeRunsByPlayer(runs) {
  const playerRuns = new Map()

  runs.forEach(run => {
    const key = run.player_id || run.player_name
    if(!playerRuns.has(key)) {
      playerRuns.set(key, run)
      return
    }

    playerRuns.set(key, mergeRunRows(playerRuns.get(key), run))
  })

  const mergedRuns = Array.from(playerRuns.values())
  const sortKey = mergedRuns.some(run => run.avg_eteneminen_time !== undefined)
    ? 'avg_eteneminen_time'
    : mergedRuns.some(run => run.avg_ball_at_base_time !== undefined)
      ? 'avg_ball_at_base_time'
      : null

  if(!sortKey) return mergedRuns

  return mergedRuns.sort((a, b) => {
    const aValue = runValue(a[sortKey])
    const bValue = runValue(b[sortKey])

    if(aValue === null && bValue === null) return 0
    if(aValue === null) return 1
    if(bValue === null) return -1
    return aValue - bValue
  })
}

const auth = {
  namespaced: true,
  state: () => ({
    runs: [],
    offset: 0,
    limit: 10,
    base: 1,
    loading_runs: false,
    player_name: '',
    range: { shortcut: 'summer' },
    markType: 'lento',
    team: null,
    isPlay: false,
    loadingIndividualRuns: false,
    individualRuns: [],
    currentPlayer: {
      id: null,
      name: null,
      img: null
    }
  }),
  mutations: {
    SET_LOADING_RUNS(state, payload) {
      state.loading_runs = payload
    },
    SET_RUNS(state, payload) {
      state.runs = payload
    },
    SET_BASE(state, payload) {
      state.base = payload
    },
    SET_RANGE(state, payload) {
      state.range = payload
    },
    SET_MARK_TYPE(state, payload) {
      state.markType = payload
    },
    SET_TEAM(state, payload) {
      state.team = payload
    },
    SET_IS_PLAY(state, payload) {
      state.isPlay = payload
    },
    SET_LOADING_INDIVIDUAL_RUNS(state, payload) {
      state.loadingIndividualRuns = payload
    },
    SET_INDIVIDUAL_RUNS(state, payload) {
      state.individualRuns = payload
    },
    SET_CURRENT_PLAYER(state, payload) {
      state.currentPlayer = payload
    }
  },
  actions: {
    async getRuns({ commit, getters, dispatch }, { league_id, league_name }) {
      commit('SET_LOADING_RUNS', true)
      commit('SET_RUNS', [])
      if(!getters.range) {
        const range = await dispatch('formEmptyRange', league_name === 'myp' ? -1 : 0)
        commit('SET_RANGE', range)
      }
      return new Promise((resolve, reject) => {

        const requests = rangeSegments(getters.range).map(segment => {
          const markTypeParam = segment.useMarkType ? `&mark_type=${getters.markType}` : ''
          const url = `/league/${league_id}/times?base=${getters.base}&start=${segment.start}&end=${segment.end}${markTypeParam}&club_ids=[${getters.team}]&is_play=${getters.isPlay}`
          return a.get(url)
        })

        Promise.all(requests)
          .then(responses => {
            const runs = mergeRunsByPlayer(responses.map(res => res.data).flat())
            commit('SET_RUNS', runs)
            resolve(runs)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_LOADING_RUNS', false)
          })
      })
    },
    getPlayerRuns({ getters, commit }, { player_id, league_id }) {
      commit('SET_LOADING_INDIVIDUAL_RUNS', true)
      commit('SET_INDIVIDUAL_RUNS', [])

      const requests = rangeSegments(getters.range).map(segment => {
        const markTypeParam = segment.useMarkType ? `&mark_type=${getters.markType}` : ''
        return a.get(`/league/${league_id}/player/${player_id}/times?base=${getters.base}&start=${segment.start}&end=${segment.end}${markTypeParam}&is_play=${getters.isPlay}`)
      })

      Promise.all(requests)
        .then(responses => {
          commit('SET_INDIVIDUAL_RUNS', responses.map(res => res.data).flat())
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          commit('SET_LOADING_INDIVIDUAL_RUNS', false)
        })
    },
    async formEmptyRange(_, yearOffset = 0) {
      const year = new Date().getFullYear() + yearOffset;
      const start = Math.floor(new Date(year, 0, 1).getTime());
      const end = Math.floor(new Date(year, 11, 31, 23, 59, 59, 999).getTime());

      // Set shortcut based on the yearOffset
      let shortcut = 'year';
      if (yearOffset === -1) {
        shortcut = '-year';
      }

      return { start, end, shortcut };
    }
  },
  getters: {
    runs: state => state.runs,
    loading_runs: state => state.loading_runs,
    base: state => state.base,
    range: state => state.range,
    markType: state => state.markType,
    team: state => state.team,
    isPlay: state => state.isPlay,
    loadingIndividualRuns: state => state.loadingIndividualRuns,
    individualRuns: state => state.individualRuns,
    currentPlayer: state => state.currentPlayer
  }
}

export default auth